import React, { useState, useEffect, useCallback } from 'react';
import './email-sources.css';
import { getSupabase } from '../lib/supabase.js';

// Public URLs for the two connection steps. We use the goodbuysgroup.com
// short URLs (configured in _redirects) so the underlying targets can
// move without code edits.
const OAUTH_CONNECT_URL   = 'https://goodbuysgroup.com/connect-gmail';
const TELEGRAM_BOT_URL    = 'https://t.me/good_buys_bot';
const TELEGRAM_BOT_HANDLE = '@good_buys_bot';

const MATCH_TYPES = [
  { value: 'domain',  label: 'Domain',        hint: 'e.g. coinbase.com — catches every From: at that domain' },
  { value: 'address', label: 'Exact address', hint: 'e.g. noreply@coinbase.com — exact From: match only' },
];

// Gmail-supported label color combos. Both values MUST come from Gmail's
// allowed palette — picking arbitrary hex breaks label creation.
// https://developers.google.com/gmail/api/reference/rest/v1/users.labels#color
const COLOR_PALETTE = [
  { bg: '#149e60', text: '#ffffff', name: 'Green' },
  { bg: '#1c4587', text: '#ffffff', name: 'Navy' },
  { bg: '#cc3a21', text: '#ffffff', name: 'Red' },
  { bg: '#eaa041', text: '#ffffff', name: 'Orange' },
  { bg: '#8e63ce', text: '#ffffff', name: 'Purple' },
  { bg: '#0b804b', text: '#ffffff', name: 'Forest' },
  { bg: '#e07798', text: '#ffffff', name: 'Pink' },
  { bg: '#cf8933', text: '#ffffff', name: 'Brown' },
  { bg: '#41236d', text: '#ffffff', name: 'Plum' },
  { bg: '#076239', text: '#ffffff', name: 'Evergreen' },
  { bg: '#3c78d8', text: '#ffffff', name: 'Sky' },
  { bg: '#fad165', text: '#1c4587', name: 'Gold' },
  { bg: '#c9daf8', text: '#1c4587', name: 'Light blue' },
  { bg: '#666666', text: '#ffffff', name: 'Slate' },
  { bg: '#fbc8d9', text: '#83334c', name: 'Blush' },
  { bg: '#a4c2f4', text: '#1c4587', name: 'Periwinkle' },
];

// Inline color-picker. Renders the current color as a clickable pill;
// clicking opens a flyout grid of palette options. Calls onPick(color)
// with the selected {bg, text}, or onPick(null) for "no color".
function ColorPicker({ bg, text, onPick }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  const swatchStyle = bg
    ? { background: bg, color: text || '#fff' }
    : { background: '#f1f5f9', color: '#94a3b8', borderStyle: 'dashed' };
  return (
    <span className="es-cp" ref={ref}>
      <button
        type="button"
        className="es-cp-swatch"
        style={swatchStyle}
        onClick={() => setOpen(!open)}
        aria-label="Pick color"
      >{bg ? 'Aa' : '—'}</button>
      {open && (
        <div className="es-cp-pop">
          <div className="es-cp-grid">
            {COLOR_PALETTE.map((c) => (
              <button
                key={c.bg}
                type="button"
                className="es-cp-opt"
                style={{ background: c.bg, color: c.text }}
                title={c.name}
                onClick={() => { onPick(c); setOpen(false); }}
              >Aa</button>
            ))}
          </div>
          <button type="button" className="es-cp-clear" onClick={() => { onPick(null); setOpen(false); }}>
            No color
          </button>
        </div>
      )}
    </span>
  );
}

export default function EmailSources({ profile, navigate, member }) {
  const isAdmin = profile?.role === 'admin';
  // When an admin is ACTING-AS an investor, profile.role is still 'admin' but a
  // `member` is set — show that investor's OWN connectivity view (scoped to
  // their household), not the admin console. Pure admin (no member) gets the
  // admin console; real non-admins get their own view.
  const actingAs = isAdmin && !!(member && member.id);
  if (!isAdmin || actingAs) {
    return <MemberEmailHub profile={profile} member={member} navigate={navigate} />;
  }
  return <AdminEmailSources profile={profile} navigate={navigate} />;
}

// ─── ConnectivityInstructions ────────────────────────────────────────────
// Two-step onboarding for an account holder:
//   1. Connect Gmail — once per household Gmail. The holder repeats this
//      step for each household member's mailbox by switching Google
//      accounts in their browser. Sub-accounts never log in to FinOps;
//      the holder operates everything on their behalf. The OAuth callback
//      auto-captures each Google profile's display name so labels read
//      correctly (e.g. finops/member/Salsabil).
//   2. Connect Telegram bot — once for the household. The holder's chat
//      receives every alert; sub-accounts share that channel.
function ConnectivityInstructions({ memberName }) {
  const meExample = memberName ? memberName.toLowerCase().split(/\s+/)[0] : 'yourfirstname';
  return (
    <section className="es-card es-connect-card">
      <h2 className="es-card-title">Get connected</h2>
      <p className="es-card-sub">
        Two short steps so the FinOps agent can track your household's
        activity automatically and ping you when it needs a decision.
      </p>

      <ol className="es-steps">
        <li className="es-step">
          <div className="es-step-num">1</div>
          <div className="es-step-body">
            <h3 className="es-step-title">
              Connect each household Gmail
              <span className="es-step-scope">per inbox</span>
            </h3>
            <p>
              You'll do this once for <strong>each Gmail account in your household</strong> —
              yours, your spouse's, and any sub-accounts. We match the connected
              Gmail back to the right household member by the email address you
              entered for them in the <strong>Household emails</strong> section
              below — so set those first if you haven't, then connect each Gmail
              in turn.
            </p>
            <ul className="es-step-list">
              <li>Sign into the <strong>first Gmail</strong> you want to connect (e.g. yours).</li>
              <li>Open the link below → click <strong>Advanced → Go to FinOps (unsafe)</strong> if Google warns "not verified" → tap <strong>Allow</strong>.</li>
              <li>You're done with that inbox when you see <strong>"✅ Connected!"</strong></li>
              <li><strong>For the next household Gmail:</strong> sign out of Google (or open an incognito window), sign in as the next person, and revisit the link.</li>
            </ul>
            <a className="es-connect-btn" href={OAUTH_CONNECT_URL} target="_blank" rel="noopener noreferrer">
              Connect Gmail →
            </a>
            <p className="es-step-foot">
              Or paste: <code className="es-url">{OAUTH_CONNECT_URL}</code>
            </p>
          </div>
        </li>

        <li className="es-step">
          <div className="es-step-num">2</div>
          <div className="es-step-body">
            <h3 className="es-step-title">
              Connect the Telegram bot
              <span className="es-step-scope">once per household</span>
            </h3>
            <p>
              <strong>One Telegram chat per household</strong>, on your phone — typically
              yours as account holder. Sub-accounts don't need their own.
              Open <strong>{TELEGRAM_BOT_HANDLE}</strong> and send these two messages:
            </p>
            <ol className="es-tg-cmds">
              <li><code>/start</code></li>
              <li><code>/me {meExample}</code>{memberName ? '' : ' (your first name in lowercase)'}</li>
            </ol>
            <p>
              The bot replies <strong>"✅ Linked!"</strong> — agent notifications about
              every household member's trades come straight to you.
            </p>
            <a className="es-connect-btn" href={TELEGRAM_BOT_URL} target="_blank" rel="noopener noreferrer">
              Open {TELEGRAM_BOT_HANDLE} →
            </a>
          </div>
        </li>
      </ol>
    </section>
  );
}

function AdminEmailSources({ profile, navigate }) {
  const sb = getSupabase();
  const isAdmin = profile?.role === 'admin';

  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState({ name: '', match_type: 'domain', match_value: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState(null);

  const load = useCallback(async () => {
    if (!sb) { setStatus('error'); setError(new Error('Supabase not available')); return; }
    setStatus('loading');
    const { data, error: err } = await sb
      .from('inv_email_senders')
      .select('*')
      .order('active', { ascending: false })
      .order('name', { ascending: true });
    if (err) { setStatus('error'); setError(err); return; }
    setRows(data || []);
    setStatus('ready');
  }, [sb]);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!isAdmin) return;
    const name = draft.name.trim();
    const match_value = draft.match_value.trim();
    if (!name || !match_value) return;
    setSaving(true);
    const { error: err } = await sb.from('inv_email_senders').insert({
      name,
      match_type: draft.match_type,
      match_value,
      notes: draft.notes.trim() || null,
    });
    setSaving(false);
    if (err) { setError(err); return; }
    setDraft({ name: '', match_type: 'domain', match_value: '', notes: '' });
    await load();
  };

  const toggleActive = async (row) => {
    if (!isAdmin) return;
    const { error: err } = await sb
      .from('inv_email_senders')
      .update({ active: !row.active })
      .eq('id', row.id);
    if (err) { setError(err); return; }
    await load();
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditDraft({
      name: row.name,
      match_type: row.match_type,
      match_value: row.match_value,
      notes: row.notes || '',
    });
  };

  const cancelEdit = () => { setEditingId(null); setEditDraft(null); };

  const saveEdit = async () => {
    if (!isAdmin || !editingId || !editDraft) return;
    const { error: err } = await sb
      .from('inv_email_senders')
      .update({
        name: editDraft.name.trim(),
        match_type: editDraft.match_type,
        match_value: editDraft.match_value.trim(),
        notes: editDraft.notes.trim() || null,
      })
      .eq('id', editingId);
    if (err) { setError(err); return; }
    cancelEdit();
    await load();
  };

  const deleteRow = async (row) => {
    if (!isAdmin) return;
    if (!window.confirm(`Delete "${row.name}"? This removes it from the forward list (existing forwarded emails are unaffected).`)) return;
    const { error: err } = await sb.from('inv_email_senders').delete().eq('id', row.id);
    if (err) { setError(err); return; }
    await load();
  };

  const updateSenderColor = async (row, color) => {
    if (!isAdmin) return;
    const patch = color
      ? { bg_color: color.bg, text_color: color.text }
      : { bg_color: null, text_color: null };
    const { error: err } = await sb.from('inv_email_senders').update(patch).eq('id', row.id);
    if (err) { setError(err); return; }
    await load();
  };

  return (
    <div className="email-sources-screen">
      <header className="es-header">
        <div>
          <div className="es-eyebrow">FinOps</div>
          <h1 className="es-title">Connectivity</h1>
          <p className="es-sub">
            How members hook up their Gmail and Telegram so the agent can do its job.
            Manage senders, connected inboxes, and extraction rules below.
          </p>
        </div>
        {navigate && (
          <div>
            <button className="es-back" onClick={() => navigate('home')}>← Back</button>
          </div>
        )}
      </header>

      <ConnectivityInstructions />


      <section className="es-card">
        <h2 className="es-card-title">Senders ({rows.filter(r => r.active).length} active / {rows.length} total)</h2>

        {status === 'loading' && <p className="es-muted">Loading…</p>}
        {status === 'error'   && <p className="es-error">Couldn't load senders: {String(error?.message || error)}</p>}

        {status === 'ready' && (
          <div className="es-table-wrap">
            <table className="es-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Match</th>
                  <th>Value</th>
                  <th>Notes</th>
                  <th>Color</th>
                  <th>Active</th>
                  {isAdmin && <th></th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => editingId === r.id ? (
                  <tr key={r.id} className="es-row es-row-editing">
                    <td><input value={editDraft.name} onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })} /></td>
                    <td>
                      <select value={editDraft.match_type} onChange={(e) => setEditDraft({ ...editDraft, match_type: e.target.value })}>
                        {MATCH_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </td>
                    <td><input value={editDraft.match_value} onChange={(e) => setEditDraft({ ...editDraft, match_value: e.target.value })} /></td>
                    <td><input value={editDraft.notes} onChange={(e) => setEditDraft({ ...editDraft, notes: e.target.value })} placeholder="optional" /></td>
                    <td>
                      <ColorPicker
                        bg={r.bg_color}
                        text={r.text_color}
                        onPick={(c) => updateSenderColor(r, c)}
                      />
                    </td>
                    <td>{r.active ? 'Yes' : 'No'}</td>
                    <td className="es-actions">
                      <button className="es-link" onClick={saveEdit}>Save</button>
                      <button className="es-link es-link-muted" onClick={cancelEdit}>Cancel</button>
                    </td>
                  </tr>
                ) : (
                  <tr key={r.id} className={'es-row' + (r.active ? '' : ' es-row-inactive')}>
                    <td className="es-name">{r.name}</td>
                    <td className="es-mono">{r.match_type}</td>
                    <td className="es-mono">{r.match_value}</td>
                    <td className="es-notes">{r.notes || <span className="es-muted">—</span>}</td>
                    <td>
                      {isAdmin ? (
                        <ColorPicker
                          bg={r.bg_color}
                          text={r.text_color}
                          onPick={(c) => updateSenderColor(r, c)}
                        />
                      ) : (
                        <span className="es-cp-swatch es-cp-swatch-ro" style={r.bg_color ? { background: r.bg_color, color: r.text_color } : { background: '#f1f5f9', color: '#94a3b8' }}>
                          {r.bg_color ? 'Aa' : '—'}
                        </span>
                      )}
                    </td>
                    <td>
                      {isAdmin ? (
                        <button className={'es-toggle' + (r.active ? ' es-toggle-on' : '')} onClick={() => toggleActive(r)} aria-label="Toggle active">
                          {r.active ? 'On' : 'Off'}
                        </button>
                      ) : (r.active ? 'Yes' : 'No')}
                    </td>
                    {isAdmin && (
                      <td className="es-actions">
                        <button className="es-link" onClick={() => startEdit(r)}>Edit</button>
                        <button className="es-link es-link-danger" onClick={() => deleteRow(r)}>Delete</button>
                      </td>
                    )}
                  </tr>
                ))}
                {!rows.length && (
                  <tr><td colSpan={isAdmin ? 7 : 6} className="es-muted" style={{ padding: 24, textAlign: 'center' }}>
                    No senders yet. Add one below.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {isAdmin && (
        <section className="es-card">
          <h2 className="es-card-title">Add a sender</h2>
          <form className="es-form" onSubmit={handleAdd}>
            <label className="es-field">
              <span>Name</span>
              <input
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                placeholder="Coinbase"
                required
              />
            </label>
            <label className="es-field">
              <span>Match type</span>
              <select value={draft.match_type} onChange={(e) => setDraft({ ...draft, match_type: e.target.value })}>
                {MATCH_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <small>{MATCH_TYPES.find(t => t.value === draft.match_type)?.hint}</small>
            </label>
            <label className="es-field">
              <span>Match value</span>
              <input
                value={draft.match_value}
                onChange={(e) => setDraft({ ...draft, match_value: e.target.value })}
                placeholder={draft.match_type === 'domain' ? 'coinbase.com' : 'noreply@coinbase.com'}
                required
              />
            </label>
            <label className="es-field es-field-wide">
              <span>Notes (optional)</span>
              <input
                value={draft.notes}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                placeholder="What this entity sends"
              />
            </label>
            <div className="es-form-actions">
              <button type="submit" className="es-btn-primary" disabled={saving}>
                {saving ? 'Adding…' : 'Add sender'}
              </button>
            </div>
          </form>
        </section>
      )}

      {!isAdmin && status === 'ready' && (
        <p className="es-muted" style={{ textAlign: 'center', marginTop: 8 }}>
          Only admins can edit the sender list.
        </p>
      )}

      <ConnectedInboxes isAdmin={isAdmin} />
    </div>
  );
}

// ─── ConnectedInboxes ────────────────────────────────────────────────────
// One row per member who has connected their Gmail via the OAuth webapp.
// Editable display_name and color — used by the email agent to label
// every forwarded email with finops/member/<display_name>.
// ─── People & Connections (Phase C) ─────────────────────────────────────────
// Person-centric: each household's holder + sub-accounts, the mailboxes the
// agent reads for each (from the token-free connected_inboxes view, owned via
// executor_inboxes.owner_member_id), and the holder's Telegram (phone) status.
// Admin assigns a mailbox's owner / disconnects it; writes go to the merged
// executor_inboxes record (admin/holder RLS).
function ConnectedInboxes({ isAdmin, member }) {
  const sb = getSupabase();
  const [inboxes, setInboxes] = React.useState([]);
  const [members, setMembers] = React.useState([]);
  const [runnersById, setRunnersById] = React.useState({});
  const [status, setStatus] = React.useState('loading');
  const [error, setError] = React.useState(null);

  const load = React.useCallback(async () => {
    if (!sb) { setStatus('error'); setError(new Error('Supabase not available')); return; }
    setStatus('loading');
    const [inbRes, memRes, runRes] = await Promise.all([
      sb.from('connected_inboxes').select('*'),
      sb.from('members').select('id, name, display_name, email, managed_by_member_id, active').eq('active', true),
      sb.from('runners').select('id, data'),
    ]);
    if (inbRes.error) { setStatus('error'); setError(inbRes.error); return; }
    setInboxes(inbRes.data || []);
    setMembers(memRes.data || []);
    const rmap = {}; (runRes.data || []).forEach((r) => { rmap[r.id] = r.data || {}; });
    setRunnersById(rmap);
    setStatus('ready');
  }, [sb]);

  React.useEffect(() => { load(); }, [load]);

  const assignOwner = async (email, memberId) => {
    const { error: err } = await sb.from('executor_inboxes').update({ owner_member_id: memberId || null }).eq('email', email);
    if (err) { setError(err); return; }
    await load();
  };
  const setRevoked = async (email, revoked) => {
    const { error: err } = await sb.from('executor_inboxes').update({ revoked_at: revoked ? new Date().toISOString() : null }).eq('email', email);
    if (err) { setError(err); return; }
    await load();
  };

  // Build households + group mailboxes by owner.
  const byId = Object.fromEntries(members.map((m) => [m.id, m]));
  const holders = members.filter((m) => !m.managed_by_member_id);
  const subsByHolder = {};
  members.filter((m) => m.managed_by_member_id).forEach((m) => {
    (subsByHolder[m.managed_by_member_id] = subsByHolder[m.managed_by_member_id] || []).push(m);
  });
  const inboxesByOwner = {};
  inboxes.forEach((i) => {
    const k = i.owner_member_id || '__none__';
    (inboxesByOwner[k] = inboxesByOwner[k] || []).push(i);
  });
  const nameOf = (id) => { const m = byId[id]; return m ? (m.display_name || m.name) : id; };
  const telegramLinked = (id) => !!(runnersById[id] && runnersById[id].telegram_chat_id);
  const myHolderId = member ? (member.managed_by_member_id || member.id) : null;
  const visibleHolders = isAdmin ? holders : holders.filter((h) => h.id === myHolderId);

  // Who the viewer may manage. Admin → everyone. A holder → their own
  // household (themselves + sub-accounts), so they can assign/reassign and
  // disconnect their household's mailboxes without the admin console.
  const householdIds = new Set();
  if (myHolderId) {
    householdIds.add(myHolderId);
    members.filter((m) => m.managed_by_member_id === myHolderId).forEach((m) => householdIds.add(m.id));
  }
  const householdEmails = new Set(
    members.filter((m) => householdIds.has(m.id) && m.email).map((m) => String(m.email).toLowerCase())
  );
  const canManage = isAdmin || !!member;
  // The owner dropdown is scoped to the household for a holder (they can't
  // hand a mailbox to another family); admins see every member.
  const memberOptions = (isAdmin ? members : members.filter((m) => householdIds.has(m.id)))
    .map((m) => ({ id: m.id, name: nameOf(m.id) }));
  // A holder may edit a mailbox their household owns, or claim an ownerless one
  // whose address they've recorded for a household member (mirrors the RLS).
  const canEditInbox = (i) => isAdmin
    || (i.owner_member_id && householdIds.has(i.owner_member_id))
    || (!i.owner_member_id && householdEmails.has(String(i.email || '').toLowerCase()));

  const allUnassigned = inboxesByOwner['__none__'] || [];
  // Admin sees every unassigned mailbox; a holder only sees the ones that
  // match an address they've recorded (so we don't leak other households' mail).
  const unassigned = isAdmin
    ? allUnassigned
    : allUnassigned.filter((i) => householdEmails.has(String(i.email || '').toLowerCase()));

  const renderMailbox = (i) => (
    <div key={i.email} className="es-row" style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', flexWrap: 'wrap' }}>
      <span className="es-mono" style={{ flex: '1 1 220px' }}>{i.email}</span>
      <span className="lc-tag" style={i.status === 'revoked' ? { background: '#fee2e2', color: '#991b1b' } : { background: '#dcfce7', color: '#166534' }}>
        {i.status === 'revoked' ? 'disconnected' : (i.status === 'connected' ? 'reading' : 'pending')}
      </span>
      {canEditInbox(i) && (
        <React.Fragment>
          <select className="btn btn-ghost btn-sm" value={i.owner_member_id || ''} onChange={(e) => assignOwner(i.email, e.target.value)}>
            <option value="">— unassigned —</option>
            {memberOptions.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
          {i.status === 'revoked'
            ? <button className="es-link" onClick={() => setRevoked(i.email, false)}>Reconnect</button>
            : <button className="es-link es-link-danger" onClick={() => setRevoked(i.email, true)}>Disconnect</button>}
        </React.Fragment>
      )}
    </div>
  );

  return (
    <section className="es-card">
      <h2 className="es-card-title">People &amp; Connections</h2>
      <p className="es-card-sub">
        Each household's people and the mailboxes the agent reads for them. Connect a Gmail,
        then assign it to the right person. The phone (Telegram) is one per household holder.
      </p>

      {status === 'loading' && <p className="es-muted">Loading…</p>}
      {status === 'error'   && <p className="es-error">Couldn't load: {String(error?.message || error)}</p>}

      {status === 'ready' && visibleHolders.map((h) => {
        const people = [h, ...((subsByHolder[h.id] || []))];
        return (
          <div key={h.id} style={{ borderTop: '1px solid var(--border2, #e5e7eb)', paddingTop: 12, marginTop: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <strong>{nameOf(h.id)}</strong>
              <span className="lc-tag" style={{ background: '#eef2ff', color: '#3730a3' }}>account holder</span>
              <span style={{ marginLeft: 'auto', fontSize: 13 }}>
                📱 {telegramLinked(h.id) ? 'Telegram linked' : <span style={{ color: '#b45309' }}>not linked</span>}
              </span>
            </div>
            {people.map((p) => (
              <div key={p.id} style={{ margin: '6px 0 6px 12px' }}>
                <div style={{ fontSize: 13, color: 'var(--text2, #475569)' }}>
                  {nameOf(p.id)}{p.managed_by_member_id ? ' · sub-account' : ''}
                </div>
                {(inboxesByOwner[p.id] || []).length
                  ? (inboxesByOwner[p.id] || []).map(renderMailbox)
                  : <div className="es-muted" style={{ fontSize: 12, marginLeft: 4 }}>no mailboxes yet</div>}
              </div>
            ))}
          </div>
        );
      })}

      {status === 'ready' && canManage && unassigned.length > 0 && (
        <div style={{ borderTop: '1px solid var(--border2, #e5e7eb)', paddingTop: 12, marginTop: 12 }}>
          <strong style={{ color: '#b45309' }}>Unassigned mailboxes ({unassigned.length})</strong>
          <p className="es-muted" style={{ fontSize: 12 }}>
            {isAdmin
              ? 'Connected but not tied to anyone yet — assign each to its owner.'
              : 'Connected and matched to an address you recorded — assign each to the right person.'}
          </p>
          {unassigned.map(renderMailbox)}
        </div>
      )}

      <p style={{ marginTop: 14 }}>
        <a className="es-connect-btn" href={OAUTH_CONNECT_URL} target="_blank" rel="noopener noreferrer">+ Connect a Gmail</a>
      </p>
    </section>
  );
}

// ─── MemberEmailHub ─────────────────────────────────────────────────────
// One-screen contact-info manager for the household. Lists the account
// holder + every active sub-account they manage, with an inline email
// field on each row. Saves all touched rows in one Save action.
//
// Why centralize here instead of on each family-tile side panel? The
// family tile is primarily for editing allocations / deactivation /
// renaming — surfacing emails there cluttered the panel and made the
// holder click into each sub-account just to update an address. This
// hub solves it in one screen, one Save.
function MemberEmailHub({ profile, member, navigate }) {
  const sb = getSupabase();
  const [rows, setRows] = useState(null); // null = loading; [] = no household
  const [drafts, setDrafts] = useState({}); // memberId → current draft
  const [originals, setOriginals] = useState({}); // memberId → loaded value
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [flash, setFlash] = useState(null);

  const load = useCallback(async () => {
    if (!sb || !member?.id) return;
    setRows(null);
    setError(null);
    try {
      // Holder + every sub-account they manage. We don't recurse beyond
      // one level — sub-accounts don't manage sub-accounts in this schema.
      const { data: subs, error: sErr } = await sb
        .from('members')
        .select('id, name, display_name, email, managed_by_member_id, active')
        .eq('managed_by_member_id', member.id)
        .eq('active', true);
      if (sErr) throw sErr;
      const { data: self, error: hErr } = await sb
        .from('members')
        .select('id, name, display_name, email')
        .eq('id', member.id)
        .single();
      if (hErr) throw hErr;
      const all = [
        { ...self, role: 'holder' },
        ...((subs || []).map((s) => ({ ...s, role: 'sub' }))),
      ];
      const draft = {}, orig = {};
      all.forEach((m) => { draft[m.id] = m.email || ''; orig[m.id] = m.email || ''; });
      setRows(all);
      setDrafts(draft);
      setOriginals(orig);
    } catch (e) {
      setError(e?.message || String(e));
      setRows([]);
    }
  }, [sb, member?.id]);

  useEffect(() => { load(); }, [load]);

  // Save: one UPDATE per changed row. Members already have RLS write on
  // their own member row + managed sub-account rows (the existing
  // "Write financial accounts"-style policies extend to members table
  // via "Manage own enrollment"? actually members table updates use the
  // base member RLS). Errors per row are accumulated and reported.
  const handleSave = async () => {
    if (!rows || !rows.length) return;
    const changed = rows
      .map((m) => ({ id: m.id, name: m.display_name || m.name, current: (drafts[m.id] || '').trim(), original: (originals[m.id] || '').trim() }))
      .filter((row) => row.current !== row.original);
    if (!changed.length) return;
    setSaving(true);
    setError(null);
    const errors = [];
    let linkedCount = 0;
    for (const row of changed) {
      // 1) Save the member.email change.
      const { error: err } = await sb
        .from('members')
        .update({ email: row.current || null })
        .eq('id', row.id);
      if (err) { errors.push(`${row.name}: ${err.message || err}`); continue; }

      // 2) If a Gmail with this address has already been connected via the
      //    OAuth flow, attach it to this member. This is the "connect first,
      //    name later" convergence: the holder records the family member's
      //    email here and their already-connected inbox attaches itself.
      //
      //    2a) The merged record the agent + People & Connections actually
      //        read: claim the matching executor_inboxes row (set its owner).
      //        Only claims an OWNERLESS inbox (or one already owned by this
      //        member) so we never yank a mailbox off someone else; the
      //        "holder claim matching inbox" RLS policy permits exactly this.
      if (row.current) {
        const { data: exec } = await sb.from('executor_inboxes')
          .select('email, owner_member_id')
          .ilike('email', row.current)
          .maybeSingle();
        if (exec?.email && (!exec.owner_member_id || exec.owner_member_id === row.id)) {
          const { error: claimErr } = await sb.from('executor_inboxes')
            .update({ owner_member_id: row.id })
            .ilike('email', row.current);
          if (!claimErr) linkedCount++;
        }
      }

      // 3) If the email USED to point at this member's inbox (now changed),
      //    release the old attribution so we don't leave a stale link.
      if (row.original && row.original !== row.current) {
        const { data: oldExec } = await sb.from('executor_inboxes')
          .select('email, owner_member_id')
          .ilike('email', row.original)
          .maybeSingle();
        if (oldExec?.email && oldExec.owner_member_id === row.id) {
          await sb.from('executor_inboxes').update({ owner_member_id: null }).ilike('email', row.original);
        }
      }
    }
    setSaving(false);
    if (errors.length) {
      setError(`Couldn't update ${errors.length} row${errors.length === 1 ? '' : 's'}: ` + errors.join(' · '));
      return;
    }
    const linkNote = linkedCount ? ` · ${linkedCount} Gmail link${linkedCount === 1 ? '' : 's'} updated` : '';
    setFlash(`Saved ${changed.length} email${changed.length === 1 ? '' : 's'}.${linkNote}`);
    setTimeout(() => setFlash(null), 3000);
    await load();
  };

  const hasChanges = rows && rows.some((m) => (drafts[m.id] || '').trim() !== (originals[m.id] || '').trim());

  return (
    <div className="email-sources-screen">
      <header className="es-header">
        <div>
          <div className="es-eyebrow">FinOps</div>
          <h1 className="es-title">Connectivity</h1>
          <p className="es-sub">
            Hook your Gmail and Telegram up to the FinOps agent — two short
            steps below. The household-email manager at the bottom is for
            ledger receipts only; it's separate from the Gmail connect.
          </p>
        </div>
        {navigate && (
          <div>
            <button className="es-back" onClick={() => navigate('home')}>← Back</button>
          </div>
        )}
      </header>

      <ConnectivityInstructions memberName={member?.display_name || member?.name} />

      <ConnectedInboxes isAdmin={false} member={member} />

      <section className="es-card">
        <h2 className="es-card-title">Household emails (ledger contact)</h2>
        {rows === null ? (
          <p className="es-muted">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="es-muted">No household members found.</p>
        ) : (
          <div className="meh-list">
            {rows.map((m) => {
              const draft = drafts[m.id] || '';
              const orig = originals[m.id] || '';
              const changed = draft.trim() !== orig.trim();
              return (
                <div key={m.id} className={'meh-row' + (changed ? ' is-changed' : '')}>
                  <div className="meh-identity">
                    <div className="meh-name">{m.display_name || m.name}</div>
                    <div className={'meh-role meh-role-' + m.role}>{m.role === 'holder' ? 'Account holder' : 'Sub-account'}</div>
                  </div>
                  <div className="meh-field">
                    <input
                      type="email"
                      value={draft}
                      onChange={(e) => setDrafts((prev) => ({ ...prev, [m.id]: e.target.value }))}
                      disabled={saving}
                      placeholder={`${(m.display_name || m.name || 'sub').toLowerCase()}@example.com`}
                    />
                    {changed && (
                      <div className="meh-was">
                        was {orig ? <span className="mono">{orig}</span> : <em>no email</em>}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {error && <div className="meh-error">{error}</div>}
        {flash && <div className="meh-flash">{flash}</div>}

        <div className="meh-actions">
          <button
            type="button"
            className="es-btn-primary"
            disabled={saving || !hasChanges}
            onClick={handleSave}
          >
            {saving ? 'Saving…' : hasChanges ? 'Save changes' : 'No changes'}
          </button>
        </div>
      </section>

    </div>
  );
}
